<template>
  <div class="fatigue-index-card">
    <div class="fatigue-index-card__levels">
      <div
          v-for="level in normalizedLevels"
          :key="level.key"
          class="fatigue-index-card__level-row"
      >
        <div class="fatigue-index-card__level-name">
          <span
              class="fatigue-index-card__mark"
              :style="{ backgroundColor: level.color }"
          ></span>

          <span>{{ level.label }}</span>
        </div>

        <span
            class="fatigue-index-card__change"
            :class="{
              'positive-change': level.change > 0,
              'negative-change': level.change < 0,
              'neutral-change': level.change === 0
            }"
        >
          {{ getPercentChangeText(level.change) }}
        </span>

        <span class="fatigue-index-card__percent">
          {{ formatPercent(level.value) }}
        </span>
      </div>
    </div>

    <div
        class="fatigue-index-card__bar"
        :class="{ 'fatigue-index-card__bar--empty': !hasLevelSegments }"
        :style="barGridStyle"
    >
      <template v-if="hasLevelSegments">
        <div
            v-for="(level, index) in levelSegments"
            :key="level.key"
            class="fatigue-index-card__bar-part"
            :style="getBarPartStyle(index)"
        ></div>
      </template>
    </div>

    <div
        v-if="hasLevelSegments"
        class="fatigue-index-card__bar-values"
        :style="barGridStyle"
    >
      <span
          v-for="level in levelSegments"
          :key="level.key"
          class="fatigue-index-card__bar-value"
      >
        {{ formatPercent(level.value) }}
      </span>
    </div>

    <div class="fatigue-index-card__positions">
      <div class="fatigue-index-card__subtitle">
        Хроническая усталость по должностям
      </div>

      <div class="fatigue-index-card__position-list">
        <div
            v-for="position in normalizedPositions"
            :key="position.name"
            class="fatigue-index-card__position-row"
        >
          <div class="fatigue-index-card__position-name">
            <span class="fatigue-index-card__position-title">
              {{ position.name }}
            </span>

            <a-popover
                v-if="position.warning"
                trigger="hover"
                placement="top"
            >
              <template #content>
                <div class="fatigue-index-card__popover">
                  {{ position.warning }}
                </div>
              </template>

              <InfoCircleOutlined class="fatigue-index-card__warning-icon" />
            </a-popover>
          </div>

          <div class="fatigue-index-card__mini-scale">
            <div class="fatigue-index-card__mini-steps">
              <span
                  v-for="step in getMiniScaleSteps(position)"
                  :key="step.index"
                  class="fatigue-index-card__mini-step"
                  :style="{ backgroundColor: step.color }"
              ></span>
            </div>
          </div>

          <div class="fatigue-index-card__position-value">
            <span class="current-position-value">{{ position.value }}</span>
            <span>/ {{ position.max }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { InfoCircleOutlined } from '@ant-design/icons-vue'

const GREY_COLOR = '#D1D1D1'
const EMPTY_STEP_COLOR = '#EEF0F3'

export default {
  name: 'ChronicFatigueIndexCard',

  components: {
    InfoCircleOutlined,
  },

  props: {
    levels: {
      type: Array,
      default: () => [],
    },
    positions: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const toNumber = (value) => {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    }

    const roundToOne = (value) => {
      return Math.round(toNumber(value) * 10) / 10
    }

    const formatPercent = (value) => {
      const normalized = roundToOne(value)

      return Number.isInteger(normalized)
          ? `${normalized}%`
          : `${normalized.toFixed(1)}%`
    }

    const getPositionColorByValue = (value, max = 10) => {
      const safeValue = toNumber(value)
      const safeMax = toNumber(max) || 10
      const ratio = safeValue / safeMax

      if (ratio <= 0.2) {
        return '#21AC0C'
      }

      if (ratio <= 0.5) {
        return '#99DA6E'
      }

      if (ratio <= 0.8) {
        return '#FF9F43'
      }

      return '#F55565'
    }

    const normalizedLevels = computed(() => {
      return props.levels.map((level) => {
        const value = roundToOne(level.value)

        return {
          ...level,
          change: roundToOne(level.change),
          value,
          color: value === 0 ? GREY_COLOR : (level.color || GREY_COLOR),
        }
      })
    })

    const levelsTotal = computed(() => {
      return normalizedLevels.value.reduce((sum, level) => {
        return sum + level.value
      }, 0)
    })

    const allLevelsAreZero = computed(() => {
      return (
          normalizedLevels.value.length > 0 &&
          normalizedLevels.value.every((level) => level.value === 0)
      )
    })

    const levelSegments = computed(() => {
      if (!normalizedLevels.value.length) {
        return []
      }

      if (allLevelsAreZero.value) {
        return normalizedLevels.value.map((level) => ({
          ...level,
          color: GREY_COLOR,
          ratio: 1,
        }))
      }

      if (!levelsTotal.value) {
        return []
      }

      return normalizedLevels.value
          .filter((level) => level.value > 0)
          .map((level) => ({
            ...level,
            ratio: level.value,
          }))
    })

    const hasLevelSegments = computed(() => {
      return levelSegments.value.length > 0
    })

    const barGridStyle = computed(() => {
      if (!levelSegments.value.length) {
        return {}
      }

      return {
        gridTemplateColumns: levelSegments.value
            .map((level) => `${level.ratio}fr`)
            .join(' '),
      }
    })

    const getBarPartStyle = (index) => {
      const isFirst = index === 0
      const isLast = index === levelSegments.value.length - 1
      const currentLevel = levelSegments.value[index]

      return {
        backgroundColor: currentLevel.color,
        borderTopLeftRadius: isFirst ? '999px' : '0',
        borderBottomLeftRadius: isFirst ? '999px' : '0',
        borderTopRightRadius: isLast ? '999px' : '0',
        borderBottomRightRadius: isLast ? '999px' : '0',
      }
    }

    const normalizedPositions = computed(() => {
      return props.positions.map((position) => {
        const max = toNumber(position.max) || 10
        const rawValue = toNumber(position.value)
        const value = Math.max(0, Math.min(rawValue, max))
        const color = getPositionColorByValue(value, max)

        return {
          ...position,
          value,
          max,
          color,
          warning: value > 5
              ? 'Рекомендуется снизить нагрузку на специалистов'
              : '',
        }
      })
    })

    const getMiniScaleSteps = (position) => {
      const totalSteps = 10
      const filledSteps = Math.round((position.value / position.max) * totalSteps)

      return Array.from({ length: totalSteps }, (_, index) => ({
        index,
        color: index < filledSteps ? position.color : EMPTY_STEP_COLOR,
      }))
    }

    const getPercentChangeText = (value) => {
      const normalized = roundToOne(value)

      if (normalized > 0) {
        return `↑ ${normalized}%`
      }

      if (normalized < 0) {
        return `↓ ${Math.abs(normalized)}%`
      }

      return '–'
    }

    return {
      normalizedLevels,
      levelSegments,
      hasLevelSegments,
      barGridStyle,
      getBarPartStyle,
      normalizedPositions,
      getMiniScaleSteps,
      getPercentChangeText,
      formatPercent,
    }
  },
}
</script>

<style scoped lang="scss">
.fatigue-index-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
}

.fatigue-index-card__levels {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
}

.fatigue-index-card__level-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(30px, 40px) minmax(30px, 60px);
  align-items: center;
  column-gap: 10px;
  min-width: 0;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.fatigue-index-card__level-name {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.fatigue-index-card__level-name span:last-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fatigue-index-card__mark {
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.fatigue-index-card__change {
  min-width: 0;
  text-align: right;
  white-space: nowrap;
}

.fatigue-index-card__percent {
  min-width: 50px;
  text-align: right;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.88);
}

.fatigue-index-card__bar {
  display: grid;
  width: 100%;
  height: 8px;
  gap: 2px;
}

.fatigue-index-card__bar--empty {
  display: flex;
  background: #D9DEE7;
  border-radius: 999px;
}

.fatigue-index-card__bar-part {
  height: 100%;
  min-width: 0;
}

.fatigue-index-card__bar-values {
  display: grid;
  width: 100%;
  margin-top: 4px;
  gap: 2px;
  font-size: 11px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.fatigue-index-card__bar-value {
  min-width: 0;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fatigue-index-card__positions {
  margin-top: 20px;
}

.fatigue-index-card__subtitle {
  margin-bottom: 16px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.fatigue-index-card__position-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fatigue-index-card__position-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  column-gap: 16px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.fatigue-index-card__position-name {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

.fatigue-index-card__position-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.fatigue-index-card__warning-icon {
  margin-left: 8px;
  color: #FF9F43;
  font-size: 13px;
  flex: 0 0 auto;
  cursor: pointer;
}

.fatigue-index-card__popover {
  max-width: 240px;
  font-size: 13px;
  line-height: 130%;
}

.fatigue-index-card__mini-scale {
  width: 38px;
  min-width: 38px;
}

.fatigue-index-card__mini-steps {
  display: grid;
  grid-template-columns: repeat(10, 2px);
  column-gap: 2px;
  align-items: center;
  justify-content: start;
  width: 38px;
  height: 8px;
}

.fatigue-index-card__mini-step {
  display: block;
  width: 2px;
  height: 8px;
  border-radius: 999px;
  background: #EEF0F3;
}

.fatigue-index-card__position-value {
  display: flex;
  justify-content: flex-end;
  gap: 3px;
  min-width: 52px;
  white-space: nowrap;
}

.fatigue-index-card__position-value span:first-child {
  color: rgba(0, 0, 0, 0.88);
}

.fatigue-index-card__position-value span:last-child {
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

.positive-change {
  color: var(--main-green);
}

.negative-change {
  color: #F55565;
}

.neutral-change {
  color: rgba(0, 0, 0, 0.45);
}

@media (max-width: $mobile-breakpoint) {
  .fatigue-index-card__position-row {
    grid-template-columns: minmax(0, 1fr) auto;
    row-gap: 4px;
    column-gap: 16px;
  }

  .fatigue-index-card__mini-scale {
    grid-column: 1;
  }

  .fatigue-index-card__position-value {
    grid-column: 2;
    grid-row: 1 / span 2;
  }
}

.current-position-value {
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 120%;
}
</style>